import type { InstrumentId } from "@trader/shared/MarketData"
import { useEffect, useRef, useState } from "react"
import { ChevronDownIcon, ChevronUpIcon, CloseIcon, TrashIcon } from "../../ui/icons.tsx"
import { SymbolSearch } from "../instruments/SymbolSearch.tsx"
import { useInstrument } from "../instruments/useInstrument.ts"
import { setInstrument, useWorkspace } from "../workspace/workspaceStore.ts"
import { symbolsOf, visibleRows, type WatchSection } from "./watchlistModel.ts"
import { refreshQuotes, useQuote, watchQuotes } from "./watchlistQuotes.ts"
import { useWatchlist, watchlist, watchlistPanel } from "./watchlistStore.ts"

// The watchlist panel (docs/design/0014), on the right as on TradingView: symbols with their last
// price and the change since the previous close; sections that fold; + to add a symbol, a section
// from its menu; drag to reorder; click a row to open it on the active chart.

const formatNumber = (value: number, digits: number) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)

const signed = (value: number, digits: number) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${formatNumber(Math.abs(value), digits)}`

function SymbolRow({ active, id, index, onDragTo }: {
  readonly id: InstrumentId
  readonly index: number
  readonly active: boolean
  readonly onDragTo: (from: number, to: number) => void
}) {
  const instrument = useInstrument(id)
  const quote = useQuote(id)
  const ready = instrument.status === "ready" ? instrument.instrument : null
  const digits = ready?.pricePrecision ?? 2
  const symbol = ready?.symbol ?? id.split(":")[1] ?? id
  const tone = typeof quote === "object" && quote.change !== null ? (quote.change > 0 ? "up" : quote.change < 0 ? "down" : "") : ""
  return (
    <li
      className={`watch-row${active ? " watch-row-active" : ""}`}
      draggable
      data-symbol={id}
      title={ready === null ? id : `${ready.name} · ${ready.venue} · ${ready.currency}`}
      onClick={() => setInstrument(id)}
      onDragStart={(event) => event.dataTransfer.setData("text/watch-index", String(index))}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        const from = Number(event.dataTransfer.getData("text/watch-index"))
        if (Number.isInteger(from)) onDragTo(from, index)
      }}
    >
      <span className="watch-symbol">
        <span className="watch-venue">{id.split(":")[0]}</span>
        {symbol}
      </span>
      <span className="watch-last">
        {quote === "loading" ? <span className="muted">…</span> : quote === "unavailable" ? <span className="muted" title="No price stored">—</span> : formatNumber(quote.last, digits)}
      </span>
      <span className={`watch-change ${tone}`}>{typeof quote === "object" && quote.change !== null ? signed(quote.change, digits) : ""}</span>
      <span className={`watch-change ${tone}`}>
        {typeof quote === "object" && quote.changePercent !== null ? `${signed(quote.changePercent, 2)}%` : ""}
      </span>
      <button
        type="button"
        className="watch-remove"
        aria-label={`Remove ${symbol} from the watchlist`}
        title="Remove"
        onClick={(event) => {
          event.stopPropagation()
          watchlist.removeSymbol(id)
        }}
      >
        <TrashIcon />
      </button>
    </li>
  )
}

function SectionRow({ index, onDragTo, section }: {
  readonly section: WatchSection
  readonly index: number
  readonly onDragTo: (from: number, to: number) => void
}) {
  const [renaming, setRenaming] = useState(false)
  return (
    <li
      className="watch-section"
      draggable={!renaming}
      onDragStart={(event) => event.dataTransfer.setData("text/watch-index", String(index))}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        const from = Number(event.dataTransfer.getData("text/watch-index"))
        // Dropped on a header: it goes just under it, into that section.
        if (Number.isInteger(from)) onDragTo(from, from < index ? index : index + 1)
      }}
    >
      <button
        type="button"
        className="watch-fold"
        aria-expanded={!section.collapsed}
        aria-label={`${section.collapsed ? "Open" : "Fold"} ${section.title}`}
        onClick={() => watchlist.toggleSection(section.id)}
      >
        {section.collapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
      </button>
      {renaming
        ? (
          <input
            className="watch-section-input"
            aria-label="Section name"
            autoFocus
            defaultValue={section.title}
            onBlur={(event) => {
              watchlist.renameSection(section.id, event.target.value)
              setRenaming(false)
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur()
              if (event.key === "Escape") setRenaming(false)
            }}
          />
        )
        : (
          <span className="watch-section-title" onDoubleClick={() => setRenaming(true)} title="Double-click to rename">
            {section.title}
          </span>
        )}
      <button
        type="button"
        className="watch-remove"
        aria-label={`Remove section ${section.title}`}
        title="Remove the section (its symbols stay)"
        onClick={() => watchlist.removeSection(section.id)}
      >
        <TrashIcon />
      </button>
    </li>
  )
}

export function Watchlist() {
  const list = useWatchlist()
  const workspace = useWorkspace()
  const activeId = workspace.panes[workspace.active]?.instrumentId ?? null
  const [adding, setAdding] = useState(false)
  const ids = useRef(symbolsOf(list))
  ids.current = symbolsOf(list)

  // Quotes while the panel is open: now, every 15 s, and as a followed replay moves.
  useEffect(() => watchQuotes(() => ids.current), [])

  const rows = visibleRows(list)
  return (
    <aside className="watchlist" aria-label="Watchlist">
      <header className="watch-header">
        <span className="watch-title">Watchlist</span>
        <button type="button" className="icon-button" title="Add a symbol (Alt+W adds the chart's symbol)" aria-label="Add a symbol" onClick={() => setAdding(true)}>
          +
        </button>
        <button
          type="button"
          className="icon-button watch-add-section"
          title="Add a section"
          aria-label="Add a section"
          onClick={() => watchlist.addSection(`Section ${list.entries.filter((entry) => entry._tag === "Section").length + 1}`)}
        >
          ≡+
        </button>
        <button type="button" className="icon-button" title="Close the watchlist" aria-label="Close the watchlist" onClick={() => watchlistPanel.set(false)}>
          <CloseIcon />
        </button>
      </header>
      {adding && (
        <div className="watch-search">
          <SymbolSearch
            autoFocus
            label="Add a symbol to the watchlist"
            placeholder="Add symbol (e.g. GER40, TCS)"
            onChoose={(instrument) => {
              watchlist.addSymbol(instrument.id)
              setAdding(false)
              void refreshQuotes(symbolsOf(watchlist.get()))
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}
      <div className="watch-columns" aria-hidden>
        <span>Symbol</span>
        <span>Last</span>
        <span>Chg</span>
        <span>Chg%</span>
      </div>
      <ul className="watch-rows">
        {rows.map(({ entry, index }) =>
          entry._tag === "Section"
            ? <SectionRow key={entry.id} section={entry} index={index} onDragTo={watchlist.move} />
            : <SymbolRow key={entry.instrumentId} id={entry.instrumentId} index={index} active={entry.instrumentId === activeId} onDragTo={watchlist.move} />
        )}
      </ul>
      {symbolsOf(list).length === 0 && <p className="muted watch-empty">No symbols yet. Press + to add one.</p>}
    </aside>
  )
}
